import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { EasyClaimAppState } from '../easyclaim.api.client.typescript/src/index';
import { CustomerJourneyQuestionState } from '../easyclaim.app.services/CustomerJourneyQuestionState';

/**
 * The serializable subset of AppState that represents a customer's progress
 * through the claim journey for a given case. This is the data required to
 * resume the journey on the same step (instead of restarting from the
 * beginning) after a page reload.
 */
export interface CaseProgress {
	screen?: EasyClaimAppState;
	reviewReturnScreen?: EasyClaimAppState;
	isDashboardMileageSkipped: boolean;
	isAdditionalDocumentsSkipped: boolean;
	isVinPictureSkipped: boolean;
	isRegistrationDocumentSkipped: boolean;
	isVehicleInformationSkipped: boolean;
	isDamageImagesSkipped: boolean;
	damageImagesToggle?: boolean;
	isVehicleImagesSkipped: boolean;
	isDamageDescriptionSkipped: boolean;
	isDashboardMileageTextInputCompleted: boolean;
	isOdometerAndMileageScreenCompleated: boolean;
	isTermsAccepted?: boolean;
	cjQuestions: CustomerJourneyQuestionState[];
	cjQuestionsModified: boolean;
	/** Whether the fullscreen prompt was already shown to the customer for this case. */
	fullscreenPromptShown?: boolean;
}

interface AppProgressStore {
	progressByCaseId: Record<string, CaseProgress>;
	setProgress: (caseId: string, progress: Partial<CaseProgress>) => void;
	getProgress: (caseId: string) => CaseProgress | undefined;
	clearProgress: (caseId: string) => void;
}

const clearStates: Set<string> = new Set([
	'Feedback',
	'ThankYou',
	'Finished',
	'WaitForAudit',
	'ThanksForFeedback',
	'Unknown',
	'Timeout',
	'TooManyAttempts',
]);

export const useAppProgressStore = create<AppProgressStore>()(
	persist(
		(set, get) => ({
			progressByCaseId: {},
			setProgress: (caseId, progress) =>
				set((state) => {
					const newStatus = progress.screen;

					if (newStatus !== undefined && clearStates.has(newStatus)) {
						const { [caseId]: _removed, ...rest } = state.progressByCaseId;

						return { progressByCaseId: rest };
					}

					return {
						progressByCaseId: {
							...state.progressByCaseId,
							[caseId]: { ...state.progressByCaseId[caseId], ...progress },
						},
					};
				}),
			getProgress: (caseId) => get().progressByCaseId[caseId],
			clearProgress: (caseId) =>
				set((state) => {
					const { [caseId]: _removed, ...rest } = state.progressByCaseId;

					return { progressByCaseId: rest };
				}),
		}),
		{
			name: 'easyclaim-app-progress',
			storage: createJSONStorage(() => localStorage),
			version: 1,
		}
	)
);
